import { Drawer } from '@billy/design-system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchContactById } from '@features/contacts/hooks/useFetchContactById'
import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { useFetchBalanceModifiers } from '@features/transactions/hooks/useFetchBalanceModifiers'
import { useFetchContactBalancePostings } from '@features/transactions/hooks/useFetchContactBalancePostings'
import { BankPayment } from '@features/transactions/types/bankPayment'

import { useTransactionStaticActions } from '../../../../hooks/useTransactionStaticActions'
import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import { BookingsSection } from '../../elements/BookingsSection/BookingsSection'
import { DetailsSection } from '../../elements/DetailsSection'
import { HeroSection } from '../../elements/HeroSection'
import { Separator } from '../../elements/Separator'
import { TransactionDrawerSkeleton } from '../../elements/TransactionDrawerSkeleton'

interface BankPaymentViewProps {
  bankPayment: BankPayment
}

export const BankPaymentView = ({ bankPayment }: BankPaymentViewProps) => {
  const { t } = useTranslation()
  const { isVoidedEntry, transactionId, transactionNo, transaction } = useTransactionDrawerContext()

  const { contactId, createdTime, entryDate, id, subjectCurrencyId: originCurrencyId } = bankPayment

  const modifierReference = `bankPayment:${id}`
  const { contact, isLoading } = useFetchContactById(contactId)
  const { balanceModifiers, isLoading: isLoadingBalanceModifiers } = useFetchBalanceModifiers({ modifierReference })
  const { contactBalancePostings, isLoading: isLoadingContactBalancePostings } = useFetchContactBalancePostings({
    originatorReference: modifierReference,
  })
  const { handleSelect, items, modals } = useTransactionStaticActions({
    isVoided: isVoidedEntry,
    originatorId: id,
    originatorType: TransactionOriginatorType.BankPayment,
    transactionId,
  })

  const originCurrencyAmount = useMemo(
    () =>
      balanceModifiers.reduce((total, balanceModifier) => total - (balanceModifier.amount || 0), 0) +
      contactBalancePostings.reduce((total, posting) => total + (posting.amount || 0), 0),
    [balanceModifiers, contactBalancePostings],
  )

  if (isLoading || isLoadingBalanceModifiers || isLoadingContactBalancePostings || !transaction) {
    return <TransactionDrawerSkeleton transactionNo={transactionNo} />
  }

  return (
    <>
      <Drawer.Header
        actions={items}
        closable
        onAction={handleSelect}
        title={t('ledger.drawer_title', { number: transactionNo })}
      />
      <Drawer.Body withPadding={false} gap="0">
        <HeroSection
          amount={originCurrencyAmount}
          contactName={contact?.name}
          currency={originCurrencyId}
          entryDate={entryDate}
        />
        <Separator />
        <BookingsSection originatorCurrency={originCurrencyId} />
        <Separator />
        <DetailsSection createdTime={createdTime} />
        <Separator />
      </Drawer.Body>
      {modals}
    </>
  )
}
